import { firstValueFrom, Observable } from 'rxjs';
import {
  IRynReplayUserDisplay,
  IRynReplayUserResolver,
  IRynReplayUserSuggestion,
} from './ryn-replay-user-resolver';
import {
  buildReplayUserContainsFilter,
  buildReplayUserEmailContainsFilter,
  buildReplayUserEmailFilter,
  buildReplayUserExactNameFilter,
  buildReplayUserExactTokenFilter,
  formatReplayUserSuggestionLabel,
  replayUserSuggestionQueryValue,
} from './ryn-replay-user-search.utils';

/**
 * The slice of a user record this resolver reads. Narrowed so hosts and tests
 * do not need the Software Security `UserModel` or HTTP stack.
 */
export interface ReplayUserRecord {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/** The slice of a user-table client this resolver calls. */
export interface ReplayUserQuerySource {
  getUsersOdata(odataQuery: string): Observable<ReplayUserRecord[]>;
}

/**
 * Ids per display-name query. A page of sessions holds at most ~50 distinct
 * users; 25 per query keeps each URL a few hundred bytes, far from any proxy's
 * limit, at the cost of at most two round trips.
 */
const DISPLAY_NAME_CHUNK = 25;

/** Default autocomplete row cap (#31323). */
const DEFAULT_SUGGESTION_LIMIT = 8;

/** Minimum typed length before autocomplete queries the user table. */
const MIN_SUGGESTION_QUERY_LENGTH = 2;

/**
 * User resolution for the replay session list (#30072), backed by a Software
 * Security user-table query source the host already has.
 *
 * ══ RESOLUTION MUST BE UNIQUE OR IT MUST REFUSE ══
 * `findUserId` narrows a privacy-sensitive query ("show me this person's
 * recordings"). Matching the WRONG person is strictly worse than matching
 * nobody: the screen would claim Sarah's sessions while showing someone
 * else's. So every path asks for two rows and answers undefined unless exactly
 * one came back — first-row-wins is the one implementation this must never be.
 *
 * `suggestUsers` is the exception: it lists every candidate so the viewer can
 * pick one explicitly before Apply runs the unique-or-refuse path.
 *
 * Network failures are allowed to propagate. The caller distinguishes "no such
 * user" (refuse the filter, say so) from "could not ask" (the reload's error
 * path) — collapsing the second into the first would mislabel an outage as a
 * typo, the same false-zero shape the read surface refuses everywhere.
 */
export function createReplayUserResolver(
  users: ReplayUserQuerySource
): IRynReplayUserResolver {
  return {
    async displayNames(
      securityUserIds: readonly string[]
    ): Promise<ReadonlyMap<string, IRynReplayUserDisplay>> {
      const names = new Map<string, IRynReplayUserDisplay>();

      for (
        let start = 0;
        start < securityUserIds.length;
        start += DISPLAY_NAME_CHUNK
      ) {
        const chunk = securityUserIds.slice(start, start + DISPLAY_NAME_CHUNK);
        const filter = chunk.map((id) => `userId eq ${id}`).join(' or ');
        const rows = await firstValueFrom(
          users.getUsersOdata(`$filter=${filter}`)
        );

        for (const row of rows) {
          const display = toUserDisplay(row);
          if (display) {
            names.set(row.userId, display);
          }
        }
      }

      return names;
    },

    async findUserId(emailOrName: string): Promise<string | undefined> {
      const query = emailOrName.trim().toLowerCase();
      if (!query) {
        return undefined;
      }

      if (query.includes('@')) {
        return idIfUnique(
          await lookupUnique(users, buildReplayUserEmailFilter(query))
        );
      }

      const exact = buildReplayUserExactNameFilter(query);
      if (exact) {
        const exactResult = await lookupUnique(users, exact);
        if (exactResult.status === 'unique') {
          return exactResult.id;
        }
        if (exactResult.status === 'ambiguous') {
          return undefined;
        }
      }

      const tokens = query.split(/\s+/).filter((t) => t.length > 0);
      if (tokens.length === 1) {
        const tokenFilter = buildReplayUserExactTokenFilter(tokens[0]);
        if (tokenFilter) {
          const tokenResult = await lookupUnique(users, tokenFilter);
          if (tokenResult.status === 'unique') {
            return tokenResult.id;
          }
          if (tokenResult.status === 'ambiguous') {
            return undefined;
          }
        }
      }

      return idIfUnique(
        await lookupUnique(users, buildReplayUserContainsFilter(query))
      );
    },

    async suggestUsers(
      query: string,
      limit = DEFAULT_SUGGESTION_LIMIT
    ): Promise<readonly IRynReplayUserSuggestion[]> {
      const trimmed = query.trim();
      if (trimmed.length < MIN_SUGGESTION_QUERY_LENGTH) {
        return [];
      }

      const capped = Math.max(1, Math.min(limit, 20));
      const filter = trimmed.includes('@')
        ? buildReplayUserEmailContainsFilter(trimmed)
        : buildReplayUserContainsFilter(trimmed);

      const rows = await firstValueFrom(
        users.getUsersOdata(
          `$filter=${filter}&$top=${capped}&$orderby=lastName,firstName`
        )
      );

      return rows
        .map((row) => toUserSuggestion(row))
        .filter((row): row is IRynReplayUserSuggestion => !!row);
    },
  };
}

export const toUserDisplay = (
  row: ReplayUserRecord
): IRynReplayUserDisplay | undefined => {
  const name = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
  const email = row.email?.trim() || undefined;
  if (name && email) {
    return { name, email };
  }
  if (name) {
    return { name };
  }
  if (email) {
    return { name: email };
  }
  return undefined;
};

const toUserSuggestion = (
  row: ReplayUserRecord
): IRynReplayUserSuggestion | undefined => {
  const display = toUserDisplay(row);
  if (!display) {
    return undefined;
  }
  return {
    userId: row.userId,
    label: formatReplayUserSuggestionLabel(display),
    queryValue: replayUserSuggestionQueryValue(display),
  };
};

/**
 * Ask for two rows. Unique → the id. Zero → none (caller may try another
 * filter). Two → ambiguous (caller must refuse, never fall through).
 */
type UniqueLookup =
  | { status: 'unique'; id: string }
  | { status: 'none' }
  | { status: 'ambiguous' };

const lookupUnique = async (
  users: ReplayUserQuerySource,
  filter: string
): Promise<UniqueLookup> => {
  const rows = await firstValueFrom(
    users.getUsersOdata(`$filter=${filter}&$top=2`)
  );
  if (rows.length === 1) {
    return { status: 'unique', id: rows[0].userId };
  }
  if (rows.length > 1) {
    return { status: 'ambiguous' };
  }
  return { status: 'none' };
};

const idIfUnique = (result: UniqueLookup): string | undefined =>
  result.status === 'unique' ? result.id : undefined;
